import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { usePersistentCallback, useStateRef } from '@prophecy/utils/react/hooks';
import { StableChildren } from '@prophecy/utils/react/StableChildren';
import { createContext, useContextSelector } from '@prophecy/utils/react/useZustandContext';
import { isEqual } from 'lodash';
import { useEffect, useRef } from 'react';
import { EqualityFn } from 'react-redux';

import { useBaseLSPClient } from './base/BaseLSPContext';
import { LSP } from './base/types';
import { Diagnostic } from './types';

const { Context: DiagnosticContext, Provider: DiagnosticContextProvider } = createContext<Diagnostic[]>([]);

export function DiagnosticProvider({
  children,
  diagnostics: diagnosticsProp
}: {
  children: React.ReactNode;
  diagnostics?: Diagnostic[];
}) {
  const [diagnosticsRef, setDiagnostics] = useStateRef<Diagnostic[]>([]);
  const lspClient = useBaseLSPClient();

  useEffect(() => {
    // if we are getting diagnostics from props, prefer that.
    if (diagnosticsProp) {
      setDiagnostics(diagnosticsProp);
      return;
    }

    return lspClient.listen(LSP.Notification.propertiesPublishDiagnostics, {
      options: { latest: false },
      callback: function ({ params }) {
        const diagnostics = params?.diagnostics || (FROZEN_EMPTY_ARRAY as Diagnostic[]);
        if (!isEqual(diagnosticsRef.current, diagnostics)) {
          setDiagnostics(diagnostics);
        }
      }
    });
  }, [lspClient, diagnosticsRef, diagnosticsProp, setDiagnostics]);

  return (
    <DiagnosticContextProvider value={diagnosticsRef.current}>
      <StableChildren>{children}</StableChildren>
    </DiagnosticContextProvider>
  );
}

export function useAllDiagnostics(
  selector: (diagnostics: Diagnostic[]) => Diagnostic[],
  equalityFn: EqualityFn<Diagnostic[]>
) {
  return useContextSelector(DiagnosticContext, selector, equalityFn);
}

export function useListenForDiagnostics(callback: (diagnostics: Diagnostic[]) => void, enabled: boolean = true) {
  const lspClient = useBaseLSPClient();
  const persistentCallback = usePersistentCallback(callback);
  const diagnosticsRef = useRef<Diagnostic[]>([]);

  useEffect(() => {
    if (!enabled) return;

    //LspClient will be undefined in case of metadata expectations layout over rest api call for diagnostics
    return lspClient?.listen(LSP.Notification.propertiesPublishDiagnostics, {
      options: { latest: false },
      callback: function ({ params }) {
        const diagnostics = params?.diagnostics || (FROZEN_EMPTY_ARRAY as Diagnostic[]);
        if (!isEqual(diagnosticsRef.current, diagnostics)) {
          diagnosticsRef.current = diagnostics;
          persistentCallback(diagnostics);
        }
      }
    });
  }, [lspClient, persistentCallback, enabled]);
}
